"use client";

import { useState, useEffect, useCallback } from "react";
import { useSession } from "next-auth/react";
import {
  readPermisosMirror,
  writePermisosMirror,
} from "@/lib/offline/permisos-mirror";

interface PerfilSeccion {
  seccion: string;
  lectura: boolean;
  escritura: boolean;
}

interface PermisosData {
  perfiles: { id: number; nombre: string }[];
  secciones: PerfilSeccion[];
  permisos: string[];
  esAdmin: boolean;
}

// Data "vacía" para impersonación durante carga: ningún permiso, ninguna
// sección. Hace que puedeVer/puedeEscribir/puede retornen `false` mientras
// el fetch del usuario impersonado termina — así el sidebar NO muestra
// items del admin que serían incorrectos durante la transición.
const EMPTY_PERMISOS_DATA: PermisosData = {
  perfiles: [],
  secciones: [],
  permisos: [],
  esAdmin: false,
};

export function usePermisos() {
  const { data: session, status } = useSession();
  const [data, setData] = useState<PermisosData | null>(null);
  const [loading, setLoading] = useState(true);
  // Flag visible: true mientras estamos impersonando otro usuario.
  // Útil para componentes que quieran mostrar "Cargando vista..." o
  // distinguir el contexto.
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    if (status === "loading") return;

    // Detectar si admin está impersonando otro usuario via sessionStorage.
    let impersonateUserId: string | null = null;
    try {
      const raw = typeof window !== "undefined" ? sessionStorage.getItem("cacique_impersonate") : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.userId) impersonateUserId = parsed.userId;
      }
    } catch { /* ignore */ }
    setIsImpersonating(Boolean(impersonateUserId));

    // ── Caso A: IMPERSONANDO ────────────────────────────────────────────
    // No usar el mirror local: contiene los permisos del admin, no del
    // usuario impersonado. Si lo pintáramos, el admin vería brevemente su
    // propio sidebar antes de que el fetch retorne los permisos del
    // impersonado — defectuoso e inseguro.
    //
    // Inicializamos con data VACÍA (no null) para que puedeVer/puedeEscribir
    // retornen false. El sidebar se filtra a items que `useRole.canAccess`
    // resuelva por otros caminos (ej. legacy ROLE_ACCESS según el role del
    // impersonado guardado en sessionStorage).
    if (impersonateUserId) {
      setData(EMPTY_PERMISOS_DATA);
      setLoading(true);

      if (!session?.user) {
        // Sin sesión activa no podemos pedir nada — abortamos.
        setLoading(false);
        return;
      }

      fetch(`/api/usuarios/permisos?userId=${impersonateUserId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d) setData(d as PermisosData);
          // NO escribir el mirror — el mirror representa "yo" (el admin),
          // no el usuario impersonado.
        })
        .catch(() => {
          // Fetch falló: dejamos data vacía. Mejor sidebar vacío que sidebar
          // del admin disfrazado de impersonación.
        })
        .finally(() => setLoading(false));
      return;
    }

    // ── Caso B: NO IMPERSONANDO ──────────────────────────────────────────
    // 1) Pintar INMEDIATO desde mirror local si existe (offline-first).
    //    No depende de session: si hay mirror, asumimos que la última
    //    sesión válida vió estos permisos.
    const mirror = readPermisosMirror();
    let mirrorPainted = false;
    if (mirror) {
      setData(mirror.data as PermisosData);
      setLoading(false);
      mirrorPainted = true;
    }

    // 2) Sin sesión y sin mirror — nada que pedir.
    if (!session?.user && !mirrorPainted) {
      setLoading(false);
      return;
    }
    if (!session?.user && mirrorPainted) {
      // Conservar mirror — útil cuando NextAuth está caído (offline).
      return;
    }

    // 3) Fetch en background. Si OK → actualizar state y mirror.
    fetch("/api/usuarios/permisos")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setData(d as PermisosData);
        writePermisosMirror(d as PermisosData);
      })
      .catch(() => {
        /* mantener mirror si lo había */
      })
      .finally(() => setLoading(false));
  }, [session, status]);

  // Can user see this route?
  // Importante: si data es null (sin cargar todavía y sin mirror), permitimos
  // navegación — el server enforza por middleware si corresponde. Pero si
  // data está cargada (incluso si es EMPTY_PERMISOS_DATA durante impersonación),
  // respetamos su contenido — devuelve false en EMPTY mientras carga.
  const puedeVer = useCallback((ruta: string): boolean => {
    if (!data) return true; // pre-mount o sin info: dejar que server decida
    if (data.esAdmin) return true;
    return data.secciones.some((s) => ruta.startsWith(s.seccion) && s.lectura);
  }, [data]);

  const puedeEscribir = useCallback((ruta: string): boolean => {
    if (!data) return false;
    if (data.esAdmin) return true;
    return data.secciones.some((s) => ruta.startsWith(s.seccion) && s.escritura);
  }, [data]);

  const puede = useCallback((permiso: string): boolean => {
    if (!data) return false;
    if (data.esAdmin) return true;
    return data.permisos.includes(permiso);
  }, [data]);

  return {
    perfiles: data?.perfiles ?? [],
    secciones: data?.secciones ?? [],
    permisos: data?.permisos ?? [],
    esAdmin: data?.esAdmin ?? false,
    loading,
    isImpersonating,
    puedeVer,
    puedeEscribir,
    puede,
  };
}
